import { useState, useContext } from 'react';
import {
    Avatar,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList, SttNoResults } from '../all';
import { LIST_FACILITIES } from '../../lib/queryKeys';
import { PermissionRequirement } from '../../lib/permissions';
import { usePermission } from '../../lib/usePermission';
import useScsService from '../../services/ScsService';
import { sporttiaDimensions } from '../../theme/theme';

// Sport colors mapping
const sportColors: Record<number, string> = {
    1: '#4CAF50', // Tenis - Green
    2: '#2196F3', // Padel - Blue
    3: '#FF5722', // Fútbol 5 - Deep Orange
    4: '#E91E63', // Fútbol 7 - Pink
    5: '#9C27B0', // Fútbol - Purple
    6: '#8BC34A', // Golf - Light Green
    28: '#607D8B', // Multiusos - Blue Grey
    29: '#FF9800', // Baloncesto - Orange
    30: '#795548', // Balonmano - Brown
    31: '#00BCD4', // Badminton - Cyan
    32: '#FFEB3B', // Voleibol - Yellow
    33: '#03A9F4', // Calle piscina - Light Blue
    34: '#0288D1', // Piscina - Dark Blue
    35: '#673AB7', // Squash - Deep Purple
    60: '#F44336', // Pilates - Red
    70: '#00ACC1', // Natación - Cyan Dark
    72: '#7E57C2', // Yoga - Purple Light
    112: '#EC407A', // Zumba - Pink Light
    128: '#26A69A', // Pickleball - Teal
};

const getSportColor = (sportId?: number): string => {
    if (sportId === undefined) return '#9E9E9E';
    return sportColors[sportId] || '#9E9E9E';
};

// Normalize text: remove accents and convert to lowercase
const normalizeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

type Sport = {
    id: number;
    name?: string;
};

type FacilitySet = {
    id: number;
    name: string;
};

type Facility = {
    id: number;
    name: string;
    sport?: Sport;
    sets?: FacilitySet[];
};

type FacilitiesResponse = {
    rows: Facility[];
};

type Props = {
    facilities: Facility[];
    onAdd: (facility: Facility) => void;
    onDelete?: (facility: Facility) => void;
    scId?: number | null;
    disabled?: boolean;
    status?: string | null;
    trash?: boolean;
    /** Permiso para añadir o quitar instalaciones; sin él la lista es de solo lectura. */
    permission?: PermissionRequirement;
};

/**
 * Component to list facilities. A button appears to add new installations.
 * @param facilities Facilities array.
 * @param onAdd Method executed when adding new installation.
 * @param onDelete Method executed when removing an installation.
 * @param scId Sport center id.
 * @param disabled Disable functions.
 * @param status "status" parameter sent in the api request.
 * @param trash Get removed or not facilities.
 * @param permission Permission needed to add or remove facilities.
 * @returns {React.ReactElement}
 */
export function SttFacilityList({
    facilities,
    onAdd,
    onDelete,
    scId = null,
    disabled = false,
    status = null,
    trash = false,
    permission,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { can } = usePermission();
    const readOnly = disabled || (!!permission && !can(permission));
    const { t } = useTranslation();
    const scsService = useScsService();
    const [searchForm, setSearchForm] = useState('');
    const [selectingFacilityOpen, setSelectingFacilityOpen] = useState(false);

    const facilityQuery = useQuery<FacilitiesResponse>({
        queryKey: [
            LIST_FACILITIES,
            {
                id: scId || cxt?.sc?.id,
                trash,
                rows: 1000,
                ...(status && { status }),
            },
        ],
        queryFn: () =>
            scsService.getScsFacilities(scId || cxt?.sc?.id || 0, {
                trash,
                rows: 1000,
                ...(status && { status }),
            }) as Promise<FacilitiesResponse>,
        enabled: selectingFacilityOpen,
    });

    // Filter facilities locally and sort by sport name
    const filteredFacilities = facilityQuery.data?.rows
        ? facilityQuery.data.rows
              .filter((facility: any) =>
                  normalizeText(facility.name).includes(
                      normalizeText(searchForm),
                  ),
              )
              .sort((a: any, b: any) => {
                  const sportA = a.sport?.name || '';
                  const sportB = b.sport?.name || '';
                  return sportA.localeCompare(sportB);
              })
        : [];

    return (
        <>
            <SttList
                title={t('facility.facilities')}
                onAdd={
                    !readOnly && !!onAdd
                        ? () => setSelectingFacilityOpen(true)
                        : undefined
                }
                onDelete={!readOnly && onDelete ? onDelete : undefined}
                data={
                    facilities &&
                    facilities.map((facility: any) => ({
                        avatar: facility.sport ? (
                            <Avatar
                                src={cxt?.getResource(
                                    `/sports/${facility.sport.id}.png`,
                                )}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: getSportColor(
                                        facility.sport.id,
                                    ),
                                    boxShadow: 1,
                                    '& img': {
                                        width: '65%',
                                        height: '65%',
                                        objectFit: 'contain',
                                        filter: 'brightness(0) invert(1)',
                                    },
                                }}
                            />
                        ) : undefined,
                        caption: facility.name,
                        objToSelect: facility,
                    }))
                }
            />

            <Dialog
                onClose={() => {
                    setSelectingFacilityOpen(false);
                    setSearchForm('');
                }}
                open={selectingFacilityOpen}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minHeight: '50vh',
                        maxHeight: '70vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        pb: 1,
                    }}
                >
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('SelectFacility')}
                    </Typography>
                    <TextField
                        size="small"
                        placeholder={t('Search')}
                        value={searchForm}
                        onChange={(e: any) => setSearchForm(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            flex: 1,
                            maxWidth: sporttiaDimensions.searchInput.maxWidth,
                        }}
                    />
                    <IconButton
                        onClick={() => {
                            setSelectingFacilityOpen(false);
                            setSearchForm('');
                        }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 2 }}>
                    {facilityQuery.isLoading && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            paddingY={8}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {facilityQuery.isSuccess &&
                        filteredFacilities.length > 0 && (
                            <List>
                                {filteredFacilities.map((facility: any) => (
                                    <ListItemButton
                                        key={facility.id}
                                        onClick={() => {
                                            setSelectingFacilityOpen(false);
                                            setSearchForm('');
                                            onAdd(facility);
                                        }}
                                        sx={{
                                            borderRadius: 1,
                                            py: 1.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                src={cxt?.getResource(
                                                    `/sports/${facility.sport?.id}.png`,
                                                )}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    backgroundColor:
                                                        getSportColor(
                                                            facility.sport?.id,
                                                        ),
                                                    border: '2px solid',
                                                    borderColor:
                                                        'background.paper',
                                                    boxShadow: 1,
                                                    '& img': {
                                                        width: '70%',
                                                        height: '70%',
                                                        objectFit: 'contain',
                                                        filter: 'brightness(0) invert(1)',
                                                    },
                                                }}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography fontWeight={500}>
                                                    {facility.name}
                                                </Typography>
                                            }
                                            secondary={
                                                facility.sets &&
                                                facility.sets.length > 0 ? (
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        flexWrap="wrap"
                                                        sx={{
                                                            mt: 0.5,
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {facility.sets.map(
                                                            (set: any) => (
                                                                <Chip
                                                                    key={set.id}
                                                                    label={
                                                                        set.name
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        height: 22,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </Stack>
                                                ) : null
                                            }
                                            secondaryTypographyProps={{
                                                component: 'div',
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}

                    {facilityQuery.isSuccess &&
                        filteredFacilities.length === 0 && (
                            <Box paddingY={8}>
                                <SttNoResults />
                            </Box>
                        )}
                </DialogContent>
            </Dialog>
        </>
    );
}
